/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  accessAPI,
  formatFecha,
  Boton,
  Titulo,
  CampoInformacion,
  Formulario,
  Sidebar,
  tituloBase,
  TablaFiltrable
} from "../../../../shared";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

export default function Eula() {
  const [eula, setEula] = useState(null);
  const [sidebarEditar, setSidebarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [loaderGenerar, setLoaderGenerar] = useState(false);
  const [loaderEliminar, setLoaderEliminar] = useState(false);
  const { eulaId } = useParams();
  const toast = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    cargarEula();
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cargarEula() {
    accessAPI(
      "GET",
      `admin/eula/${eulaId}`,
      null,
      (respuesta) => {
        setEula(respuesta);
        document.title = respuesta.titulo;
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar el acuerdo",
          sticky: true
        });
      }
    );
  }

  function editarEulaSubmit(respuestasFormulario) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/eula/${eulaId}`,
      respuestasFormulario,
      (respuesta) => {
        setEula(respuesta);
        document.title = respuesta.titulo;
        setSidebarEditar(false);
        setLoaderEditar(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Acuerdo actualizado exitosamente"
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar el acuerdo",
          sticky: true
        });
        setLoaderEditar(false);
      }
    );
  }

  function generarEula() {
    confirmDialog({
      message: `Al generar "${eula.titulo}", todos los clientes deberán aceptarlo la próxima vez que entren a Morfeo. ¿Continuar?`,
      header: "Confirmar generación",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Generar",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-success",
      accept: () => {
        setLoaderGenerar(true);
        accessAPI(
          "PUT",
          `admin/eula/${eulaId}/generar`,
          {},
          (respuesta) => {
            setLoaderGenerar(false);
            setEula(respuesta);
            toast.current?.show({
              severity: "success",
              summary: "Generado",
              detail: "El acuerdo se mostrará a los clientes"
            });
          },
          (respuesta) => {
            setLoaderGenerar(false);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al generar el acuerdo",
              sticky: true
            });
          }
        );
      }
    });
  }

  function eliminarEula() {
    confirmDialog({
      message: `¿Eliminar el acuerdo "${eula.titulo}"?`,
      header: "Confirmar eliminación",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Eliminar",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-danger",
      accept: () => {
        setLoaderEliminar(true);
        accessAPI(
          "DELETE",
          `admin/eula/${eulaId}`,
          null,
          () => {
            navigate("/eula");
          },
          (respuesta) => {
            setLoaderEliminar(false);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al eliminar el acuerdo",
              sticky: true
            });
          }
        );
      }
    });
  }

  const tieneAceptaciones = eula?.aceptaciones?.length > 0;

  const editarFormConfig = eula
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "titulo",
                tipoitemformularioid: 1,
                etiqueta: "Título",
                requerido: true,
                respuesta: eula.titulo
              },
              {
                nombre: "texto",
                tipoitemformularioid: 11,
                etiqueta: "Texto",
                requerido: true,
                respuesta: eula.texto
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Guardar cambios",
            callback: editarEulaSubmit,
            validar: true
          }
        ]
      }
    : null;

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />

      {!eula && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "1em"
          }}
        >
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <div css={{ display: "flex", alignItems: "center", gap: "1em" }}>
              <Skeleton width="2rem" height="2rem" shape="circle" />
              <Skeleton width="15rem" height="2rem" />
            </div>
          </div>
          <Card>
            <Skeleton
              width="6rem"
              height="1.5rem"
              css={{ marginBottom: "1.5em" }}
            />
            <Skeleton width="100%" height="8rem" />
          </Card>
        </div>
      )}

      {eula && (
        <>
          <Titulo
            urlVolver="/eula"
            etiquetas={
              eula.generado
                ? [{ texto: "Generado", ok: true }]
                : [{ texto: "Borrador", alerta: true }]
            }
            botones={
              <>
                {!eula.generado && (
                  <Boton
                    verde
                    callback={generarEula}
                    loader={loaderGenerar}
                  >
                    Generar
                  </Boton>
                )}
                {!tieneAceptaciones && (
                  <Boton
                    rojo
                    transparente
                    callback={eliminarEula}
                    loader={loaderEliminar}
                  >
                    Eliminar
                  </Boton>
                )}
              </>
            }
          >
            {eula.titulo}
          </Titulo>

          <Card>
            <Titulo
              botones={
                !tieneAceptaciones && (
                  <Boton icono negro callback={() => setSidebarEditar(true)}>
                    <i className="pi pi-pencil" />
                  </Boton>
                )
              }
            >
              Detalles
            </Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Título">
                {eula.titulo}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Fecha de creación">
                {formatFecha(eula.fechaCreacion)}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Fecha de generación">
                {eula.fechaGeneracion ? formatFecha(eula.fechaGeneracion) : "—"}
              </CampoInformacion>
            </div>
            <div
              css={{
                marginTop: "1em",
                padding: "1em",
                backgroundColor: "#f8f9fa",
                borderRadius: "6px",
                whiteSpace: "pre-wrap"
              }}
            >
              {eula.texto}
            </div>
          </Card>

          {eula.generado && (
            <Card css={{ marginTop: "1em" }}>
              <Titulo>Aceptaciones ({eula.aceptaciones.length})</Titulo>
              <TablaFiltrable conFiltros={false}
                value={eula.aceptaciones}
                emptyMessage="Ningún cliente aceptó el acuerdo todavía"
                stripedRows
                paginator
                rows={10}
                nombreArchivo="aceptaciones_eula"
              >
                <Column
                  header="Cliente"
                  body={(rowData) => rowData.cliente?.razonsocial || "—"}
                />
                <Column
                  header="Usuario"
                  body={(rowData) =>
                    rowData.usuario
                      ? `${rowData.usuario.nombre || ""} ${
                          rowData.usuario.apellido || ""
                        }`.trim() || rowData.usuario.email
                      : "—"
                  }
                />
                <Column
                  header="Email"
                  body={(rowData) => rowData.usuario?.email || "—"}
                />
                <Column
                  header="Fecha de aceptación"
                  body={(rowData) => formatFecha(rowData.fecha)}
                  style={{ width: "12rem" }}
                />
              </TablaFiltrable>
            </Card>
          )}

          <Sidebar
            visible={sidebarEditar}
            position="right"
            onHide={() => setSidebarEditar(false)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Editar acuerdo</Titulo>
            {editarFormConfig && (
              <Formulario
                key={eula.id + "-" + eula.titulo}
                config={editarFormConfig}
                loader={loaderEditar}
              />
            )}
          </Sidebar>
        </>
      )}
    </>
  );
}
